import { parseDate } from "@internationalized/date";

export function getFirstDayOfMonth() {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstDate = getParseDateConversion(firstDayOfMonth);
  return firstDate;
}

export function getFirstDayOfWeek() {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstDayOfWeek = firstDayOfMonth.getDay();
  return firstDayOfWeek;
}

export function getDefaultDateRange() {
  const today = new Date();

  const endDate = new Date(today);
  endDate.setDate(today.getDate() + 4);

  return {
    from: formatDateMMDDYYYY(today),
    to: formatDateMMDDYYYY(endDate),
  };
}

function getParseDateConversion(dateObj?: any) {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");

  const formattedDate = `${year}-${month}-${day}`;

  return parseDate(formattedDate);
}

interface FormattedDateTime {
  date: string;
  time: string;
}

export function getCountDays(joinedDate: string) {
  const joinedDateTime: any = new Date(joinedDate);
  const currentDate: any = new Date();
  const timeDifference = currentDate - joinedDateTime;
  const dayDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

  return dayDifference;
}

export function formatDateAndTime(
  timestamp?: string | number | Date
): FormattedDateTime {
  if (!timestamp) return { date: "", time: "" };

  const date = new Date(timestamp);

  const formattedDate = date.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
  });

  const formattedTime = date.toLocaleString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return { date: formattedDate, time: formattedTime };
}

export function formatDateMMDDYYYY(timestamp?: string | number | Date): string {
  if (!timestamp) return "";

  const date = new Date(timestamp);

  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
}

export function formatDateTimeAndYear(
  timestamp?: string | number | Date
): FormattedDateTime {
  if (!timestamp) return { date: "", time: "" };

  const date = new Date(timestamp);

  const formattedDate = date.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

  const formattedTime = date.toLocaleString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return { date: formattedDate, time: formattedTime };
}

const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(today.getDate() - 1);

const dayBeforeYesterday = new Date(today);
dayBeforeYesterday.setDate(today.getDate() - 2);

export const formattedToday = formatDateTimeAndYear(today).date;
export const formattedYesterday = formatDateTimeAndYear(yesterday).date;
export const formattedDayBeforeYesterday =
  formatDateTimeAndYear(dayBeforeYesterday).date;

// export const formatDuration = (duration: string) => {
//   const parts = duration.split(":");
//   if (parts.length !== 3) return "--";

//   let [hours, minutes, seconds] = parts;
//   seconds = seconds.split(".")[0];

//   let totalHours = parseInt(hours, 10);
//   let totalMinutes = parseInt(minutes, 10);
//   let totalSeconds = parseInt(seconds, 10);

//   let totalDurationInSeconds =
//     totalHours * 3600 + totalMinutes * 60 + totalSeconds;

//   let days = Math.floor(totalDurationInSeconds / (24 * 3600));
//   let remainingHours = Math.floor(
//     (totalDurationInSeconds % (24 * 3600)) / 3600
//   );
//   let remainingMinutes = Math.floor((totalDurationInSeconds % 3600) / 60);
//   let remainingSeconds = totalDurationInSeconds % 60;

//   if (days > 0) {
//     return `${days} day${days > 1 ? "s" : ""} ${remainingHours
//       .toString()
//       .padStart(2, "0")}:${remainingMinutes
//       .toString()
//       .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
//   }

//   if (totalHours > 0) {
//     return `${totalHours.toString().padStart(2, "0")}:${totalMinutes
//       .toString()
//       .padStart(2, "0")}:${totalSeconds.toString().padStart(2, "0")}`;
//   }

//   return `00:${totalMinutes.toString().padStart(2, "0")}:${totalSeconds
//     .toString()
//     .padStart(2, "0")}`;
// };

export const formatDuration = (duration: string) => {
  const parts = duration?.split(":");
  if (parts?.length !== 3) return "--";

  let [hours, minutes, seconds] = parts;
  seconds = seconds.split(".")[0];
  let totalHours = parseInt(hours, 10);
  let totalMinutes = parseInt(minutes, 10);
  let totalSeconds = parseInt(seconds, 10);

  let totalDurationInSeconds =
    totalHours * 3600 + totalMinutes * 60 + totalSeconds;

  let days = Math.floor(totalDurationInSeconds / (24 * 3600));
  let remainingHours = Math.floor(
    (totalDurationInSeconds % (24 * 3600)) / 3600
  );
  let remainingMinutes = Math.floor((totalDurationInSeconds % 3600) / 60);
  let remainingSeconds = totalDurationInSeconds % 60;

  if (days > 0) {
    return `${days}d ${remainingHours}h ${remainingMinutes}m ${remainingSeconds}s`;
  }

  if (remainingHours > 0) {
    return `${remainingHours}h ${remainingMinutes}m ${remainingSeconds}s`;
  }

  if (remainingMinutes > 0) {
    return `${remainingMinutes}m ${remainingSeconds}s`;
  }

  return `${remainingSeconds}s`;
};

export function getYearMonth(dateStr: string): string {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

export function formatMonthYearLabel(monthYear: string): string {
  const [year, month] = monthYear.split("-");
  return `${new Date(parseInt(year), parseInt(month) - 1).toLocaleString(
    "en-US",
    { month: "short" }
  )} ${year}`;
}

export function getTimeAgo(dateStr: string): string {
  const createdDate = new Date(dateStr);
  const now = new Date();
  const diffInSeconds = Math.floor(
    (now.getTime() - createdDate.getTime()) / 1000
  );

  const minutes = Math.floor(diffInSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(months / 12);

  if (minutes < 60)
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  if (days < 30) return days === 1 ? "1 day ago" : `${days} days ago`;
  if (months < 12) return months === 1 ? "1 month" : `${months} months ago`;

  return years === 1 ? "1 year ago" : `${years} years ago`;
}


